//javascript
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { watchlistAPI, signalsAPI, stockDataAPI, recommendationsAPI, isApiPaused } from '../services/api'
import { useAuth } from './AuthContext'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { portfolioAPI } from '../services/api'

const MarketAutomationContext = createContext()

export const useMarketAutomation = () => {
  const context = useContext(MarketAutomationContext)
  if (!context) {
    throw new Error('useMarketAutomation must be used within a MarketAutomationProvider')
  }
  return context
}

// Helper to round to nearest tick size (0.05)
const roundToTickSize = (price) => {
  if (typeof price !== 'number' || isNaN(price)) return price;
  const tickSize = 0.05;
  return Number((Math.round(price / tickSize) * tickSize).toFixed(2));
};

// Generic retry helper
const retry = async (fn, retries = 3, delay = 1000, logFn = console.log) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (typeof logFn === 'function') {
        logFn(`Attempt ${i + 1}/${retries} failed: ${error.message}`, 'error');
      }
      if (i < retries - 1) {
        await new Promise(res => setTimeout(res, delay * (2 ** i))); // Exponential backoff
      } else {
        throw error; // Re-throw error if all retries fail
      }
    }
  }
};

export const MarketAutomationProvider = ({ children }) => {
  const { user } = useAuth()
  const [isAutomationActive, setIsAutomationActive] = useState(false)
  const [stockData, setStockData] = useState({})
  const [watchlistSymbols, setWatchlistSymbols] = useState([])
  const [lastWatchlistUpdate, setLastWatchlistUpdate] = useState(null)
  const [lastSignalGeneration, setLastSignalGeneration] = useState(null)
  const [isUpdatingWatchlist, setIsUpdatingWatchlist] = useState(false)
  const [isGeneratingSignals, setIsGeneratingSignals] = useState(false)
  const [automationLogs, setAutomationLogs] = useState([])
  
  const intervalRef = useRef(null)
  const isCurrentlyRunning = useRef(false)
  const navigate = useNavigate();

  // Add log entry
  const addLog = useCallback((message, type = 'info') => {
    const logEntry = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()
    }
    setAutomationLogs(prev => [logEntry, ...prev.slice(0, 49)]) // Keep last 50 logs
  }, [])

  // Fetch recommended symbols
  const fetchWatchlistSymbols = useCallback(async () => {
    try {
      const response = await retry(() => recommendationsAPI.getIntradayRecommendations(), 3, 1000, addLog);
      const allSymbols = []
      const recs = response.data.recommendations || []
      recs.forEach(rec => {
        if (rec.Symbol && !allSymbols.includes(rec.Symbol)) {
          allSymbols.push(rec.Symbol)
          }
        })
      setWatchlistSymbols(allSymbols)
      addLog(`Fetched ${allSymbols.length} recommended symbols from Perplexity`, 'success')
      return allSymbols
    } catch (error) {
      console.error('Failed to fetch recommended symbols:', error)
      addLog('Failed to fetch recommended symbols', 'error')
      return []
    }
  }, [addLog])

  // Update stock data for watchlist symbols
  const updateStockData = useCallback(async (symbols = watchlistSymbols) => {
    if (!symbols || symbols.length === 0) return {}

    setIsUpdatingWatchlist(true)
    addLog(`Starting stock data update for ${symbols.length} symbols`)

    try {
      const results = {}
      
      for (let i = 0; i < symbols.length; i++) {
        const symbol = symbols[i]
        
        try {
          const cleanSymbol = symbol.replace('-EQ', '')
          const response = await retry(() => stockDataAPI.getOHLC(cleanSymbol), 3, 500, addLog);
          
          if (response.data.status && response.data.data.fetched.length > 0) {
            results[cleanSymbol] = response.data.data.fetched[0]
            addLog(`✓ Updated ${cleanSymbol}: LTP ₹${response.data.data.fetched[0].ltp}`)
          } else {
            addLog(`⚠ No data for ${cleanSymbol}`, 'warning')
          }
          
          // Rate limiting - 500ms delay between calls
          if (i < symbols.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500))
          }
        } catch (err) {
          console.error(`Failed to fetch data for ${symbol}:`, err)
          addLog(`❌ Error fetching ${symbol}: ${err.message}`, 'error')
        }
      }

      setStockData(results)
      setLastWatchlistUpdate(new Date())
      addLog(`Stock data update completed for ${Object.keys(results).length} symbols`, 'success')
      
      return results
    } catch (err) {
      addLog(`Stock data update failed: ${err.message}`, 'error')
      return {}
    } finally {
      setIsUpdatingWatchlist(false)
    }
  }, [watchlistSymbols, addLog])

  // Place SL and Target orders for all executed positions that don't have one
  const placeStopLossAndTargetOrders = useCallback(async () => {
    addLog('🔍 Starting SL/Target order placement and monitoring...')
    try {
      const [positionsRes, ordersRes] = await Promise.all([
        retry(() => portfolioAPI.getPositions(), 3, 1000, addLog),
        retry(() => portfolioAPI.getOrders(), 3, 1000, addLog),
      ])
      const positions = positionsRes.data?.data?.net || []
      const orders = ordersRes.data?.data || []

      // Filter positions with non-zero quantity (executed positions)
      const executedPositions = positions.filter(pos => pos.quantity !== 0)
      addLog(`🔍 Found ${executedPositions.length} executed positions to check for SL/Target orders`)

      if (executedPositions.length === 0) {
        addLog('ℹ️ No executed positions found, skipping SL/Target order placement')
        return
      }

      for (const position of executedPositions) {
        const symbol = position.tradingsymbol;
        const isLongPosition = position.quantity > 0;
        addLog(`📊 Checking position: ${symbol} (Qty: ${position.quantity}, Type: ${isLongPosition ? 'BUY' : 'SELL'})`);

        // Check for existing SL order
        const existingSLOrder = orders.find(order =>
          order.tradingsymbol === symbol &&
          order.order_type === 'SL' && // Ensure it's SL Limit
          ['OPEN', 'TRIGGER PENDING', 'PENDING'].includes(order.status)
        );

        // Check for existing Target order (opposite of position type, LIMIT order)
        const existingTargetOrder = orders.find(order =>
          order.tradingsymbol === symbol &&
          order.order_type === 'LIMIT' &&
          order.transaction_type === (isLongPosition ? 'SELL' : 'BUY') &&
          ['OPEN', 'PENDING'].includes(order.status)
        );

        // Fetch current LTP
        const cleanSymbol = symbol.replace('-EQ', '');
        let currentLTP;
        try {
          const ohlcResponse = await retry(() => stockDataAPI.getOHLC(cleanSymbol), 3, 500, addLog);
          if (ohlcResponse.data.status && ohlcResponse.data.data.fetched.length > 0) {
            currentLTP = ohlcResponse.data.data.fetched[0].ltp;
            addLog(`📈 Current LTP for ${cleanSymbol}: ₹${currentLTP}`);
          } else {
            addLog(`❌ No LTP data available for ${cleanSymbol}, skipping SL/Target for this position.`, 'error');
            continue;
          }
        } catch (error) {
          addLog(`❌ Failed to fetch LTP for ${cleanSymbol}: ${error.message}, skipping SL/Target for this position.`, 'error');
          continue;
        }

        // Calculate SL price (0.5% from LTP)
        const STOP_LOSS_PERCENTAGE = 0.5;
        let slPrice = isLongPosition
          ? currentLTP * (1 - STOP_LOSS_PERCENTAGE / 100)
          : currentLTP * (1 + STOP_LOSS_PERCENTAGE / 100);
        slPrice = roundToTickSize(slPrice);
        addLog(`📉 Calculated SL price for ${symbol}: ₹${slPrice}`);

        // Calculate Target price (0.2% from LTP)
        const TARGET_PERCENTAGE = 0.2;
        let targetPrice = isLongPosition
          ? currentLTP * (1 + TARGET_PERCENTAGE / 100)
          : currentLTP * (1 - TARGET_PERCENTAGE / 100);
        targetPrice = roundToTickSize(targetPrice);
        addLog(`🎯 Calculated Target price for ${symbol}: ₹${targetPrice}`);

        // Place SL order if not exists
        if (!existingSLOrder) {
          addLog(`⚠️ No SL order found for ${symbol}, placing new SL order`);
          const slOrderParams = {
            tradingsymbol: symbol,
            exchange: position.exchange || 'NSE',
            transaction_type: isLongPosition ? 'SELL' : 'BUY',
            order_type: 'SL', // Changed to SL Limit
            quantity: Math.abs(position.quantity),
            price: slPrice, // For SL Limit, price is usually trigger price or slightly better
            trigger_price: slPrice,
            product: position.product || 'MIS',
            validity: 'DAY'
          };
          try {
            await retry(() => portfolioAPI.placeOrder(slOrderParams), 3, 1000, addLog);
            addLog(`✅ SL order placed for ${symbol} at ₹${slPrice}`, 'success');
            toast.success(`SL order placed for ${symbol} at ₹${slPrice}`);
          } catch (error) {
            addLog(`❌ Failed to place SL for ${symbol}: ${error.message}`, 'error');
          }
        } else {
          addLog(`✅ SL order already exists for ${symbol} (Order ID: ${existingSLOrder.order_id})`);
        }

        // Place Target order if not exists
        if (!existingTargetOrder) {
          addLog(`⚠️ No Target order found for ${symbol}, placing new Target order`);
          const targetOrderParams = {
            tradingsymbol: symbol,
            exchange: position.exchange || 'NSE',
            transaction_type: isLongPosition ? 'SELL' : 'BUY',
            order_type: 'LIMIT',
            quantity: Math.abs(position.quantity),
            price: targetPrice,
            product: position.product || 'MIS',
            validity: 'DAY'
          };
          try {
            await retry(() => portfolioAPI.placeOrder(targetOrderParams), 3, 1000, addLog);
            addLog(`✅ Target order placed for ${symbol} at ₹${targetPrice}`, 'success');
            toast.success(`Target order placed for ${symbol} at ₹${targetPrice}`);
          } catch (error) {
            addLog(`❌ Failed to place Target for ${symbol}: ${error.message}`, 'error');
          }
        } else {
          addLog(`✅ Target order already exists for ${symbol} (Order ID: ${existingTargetOrder.order_id})`);
        }

        // Simplified OCO: Monitor and cancel the other order if one is filled
        // This would ideally be a separate background process or a more frequent check.
        // For now, we'll just log if both exist and assume manual intervention or EOD square-off.
        if (existingSLOrder && existingTargetOrder) {
          addLog(`ℹ️ Both SL and Target orders exist for ${symbol}. Manual OCO monitoring required.`, 'info');
          // In a real system, you'd poll Kite for order status and cancel the other.
          // Example:
          // const latestOrders = await retry(() => portfolioAPI.getOrders(), 3, 500, addLog);
          // const slStatus = latestOrders.data.find(o => o.order_id === existingSLOrder.order_id)?.status;
          // const targetStatus = latestOrders.data.find(o => o.order_id === existingTargetOrder.order_id)?.status;
          // if (slStatus === 'COMPLETE' && targetStatus !== 'CANCELLED') {
          //   await retry(() => portfolioAPI.cancelOrder(existingTargetOrder.order_id), 3, 500, addLog);
          //   addLog(\`Cancelled Target order ${existingTargetOrder.order_id} as SL was hit for ${symbol}`, 'info');
          // } else if (targetStatus === 'COMPLETE' && slStatus !== 'CANCELLED') {
          //   await retry(() => portfolioAPI.cancelOrder(existingSLOrder.order_id), 3, 500, addLog);
          //   addLog(\`Cancelled SL order ${existingSLOrder.order_id} as Target was hit for ${symbol}`, 'info');
          // }
        }
        await new Promise(resolve => setTimeout(resolve, 1000)); // Small delay between positions
      }
    } catch (error) {
      addLog(`❌ SL/Target order placement and monitoring failed: ${error.message}`, 'error');
    }
  }, [addLog]);

  // Generate signals and auto-execute orders, then go to Intraday
  // Accept recommendations as argument, do not fetch inside
  const generateSignalsAndExecuteOrders = useCallback(async (recommendationStocks) => {
    setIsGeneratingSignals(true)
    addLog('Starting signal generation and order execution...')
    try {
      // Fetch latest positions and orders for comprehensive duplicate check
      const [latestPositionsRes, latestOrdersRes] = await Promise.all([
        retry(() => portfolioAPI.getPositions(), 3, 1000, addLog),
        retry(() => portfolioAPI.getOrders(), 3, 1000, addLog),
      ]);
      const latestPositions = latestPositionsRes.data?.data?.net || [];
      const latestOrders = latestOrdersRes.data?.data || [];

      // Create a set of symbols that already have open positions or pending orders
      const existingSymbols = new Set();
      latestPositions.forEach(p => {
        if (p.quantity !== 0) { // Consider only non-zero positions
          existingSymbols.add(p.tradingsymbol);
        }
      });
      latestOrders.forEach(o => {
        // Consider open, pending, trigger pending, partially filled orders
        if (['OPEN', 'PENDING', 'TRIGGER PENDING', 'PARTIALLY FILLED'].includes(o.status)) {
          existingSymbols.add(o.tradingsymbol);
        }
      });
      addLog(`Found ${existingSymbols.size} symbols with existing positions or open orders.`, 'info');

      // Generate signals using recommendations passed in
      const response = await retry(() => signalsAPI.generateSignals({ recommendationStocks }), 3, 1000, addLog);
      setLastSignalGeneration(new Date())
      addLog(`Signal generation completed: ${response.data.message}`, 'success')
      toast.success(`Generated ${response.data.signals?.length || 0} new signals`)
      
      // Wait for backend to finish saving signals
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Fetch latest signals from backend
      const backendSignalsRes = await retry(() => signalsAPI.getSignals({ status: 'ACTIVE', limit: 100 }), 3, 1000, addLog);
      const backendSignals = backendSignalsRes.data || []
      const generatedSymbols = new Set((response.data.signals || []).map(s => s.symbol))
      
      let executedCount = 0
      for (const signal of backendSignals) {
        // Only execute if the signal was just generated and there's no existing position/order for it
        if (generatedSymbols.has(signal.symbol) && !existingSymbols.has(signal.symbol)) {
          addLog(`Attempting to execute order for ${signal.symbol} (Signal Type: ${signal.signalType})`);
          
          // Fetch current LTP for precise order placement
          const cleanSymbol = signal.symbol.replace('-EQ', '');
          let currentLTP;
          try {
            const ohlcResponse = await retry(() => stockDataAPI.getOHLC(cleanSymbol), 3, 500, addLog);
            if (ohlcResponse.data.status && ohlcResponse.data.data.fetched.length > 0) {
              currentLTP = ohlcResponse.data.data.fetched[0].ltp;
              addLog(`Fetched current LTP for ${cleanSymbol}: ₹${currentLTP}`);
            } else {
              addLog(`❌ No LTP data available for ${cleanSymbol}, skipping execution.`, 'error');
              continue;
            }
          } catch (error) {
            addLog(`❌ Failed to fetch LTP for ${cleanSymbol}: ${error.message}, skipping execution.`, 'error');
            continue;
          }

          // Calculate order price (e.g., BUY at LTP + 0.1%, SELL at LTP - 0.1%)
          let orderPrice;
          if (signal.signalType === 'BUY') {
            orderPrice = currentLTP * (1 + 0.001); // 0.1% above LTP for BUY
          } else { // SELL
            orderPrice = currentLTP * (1 - 0.001); // 0.1% below LTP for SELL
          }
          orderPrice = roundToTickSize(orderPrice);
          addLog(`Calculated order price for ${signal.symbol}: ₹${orderPrice}`);

          // Calculate quantity (e.g., max ₹10,000 per trade)
          const maxInvestment = 10000;
          const quantity = Math.floor(maxInvestment / orderPrice);
          if (quantity === 0) {
            addLog(`⚠️ Calculated quantity for ${signal.symbol} is 0, skipping execution.`, 'warning');
            continue;
          }
          addLog(`Calculated quantity for ${signal.symbol}: ${quantity}`);

          const orderParams = {
            tradingsymbol: signal.symbol,
            exchange: signal.exchange || 'NSE',
            transaction_type: signal.signalType,
            order_type: 'LIMIT', // Always place limit orders
            quantity: quantity,
            price: orderPrice,
            product: 'MIS', // Intraday product
            validity: 'DAY'
          };

          try {
            // eslint-disable-next-line no-await-in-loop
            await retry(() => portfolioAPI.placeOrder(orderParams), 3, 1000, addLog);
            executedCount++;
            addLog(`Order placed for ${signal.symbol}`, 'success');
            toast.success(`Order placed for ${signal.symbol}`);
            existingSymbols.add(signal.symbol); // Add to existing to prevent re-execution in same cycle
            await new Promise(resolve => setTimeout(resolve, 1000)); // Small delay after successful order
          } catch (error) {
            addLog(`❌ Failed to place order for ${signal.symbol}: ${error.message}`, 'error');
          }
        } else if (generatedSymbols.has(signal.symbol) && existingSymbols.has(signal.symbol)) {
          addLog(`ℹ️ Skipping execution for ${signal.symbol} as it has an existing position or open order.`, 'info');
        }
      }
      addLog(`Total orders auto-executed: ${executedCount}`, 'success');
    } catch (error) {
      console.error('Signal generation or order execution failed:', error)
      addLog(`Signal generation/order execution failed: ${error.message}`, 'error')
      toast.error('Failed to generate signals or execute orders')
      throw error
    } finally {
      setIsGeneratingSignals(false)
      addLog('Triggering SL/Target order placement for executed positions...', 'info')
      await placeStopLossAndTargetOrders()
    }
  }, [addLog, navigate, placeStopLossAndTargetOrders])

  // Run complete automation cycle
  const runAutomationCycle = useCallback(async () => {
    if (isCurrentlyRunning.current || !user?.kiteConnected) {
      if (isCurrentlyRunning.current) addLog('Automation cycle already running, skipping this interval.', 'info');
      if (!user?.kiteConnected) addLog('Kite not connected, skipping automation cycle.', 'warning');
      return;
    }
    isCurrentlyRunning.current = true
    addLog('🚀 Starting automation cycle', 'info')
    try {
      // Step 1: Fetch latest recommendations
      const recRes = await retry(() => recommendationsAPI.getIntradayRecommendations(), 3, 1000, addLog);
      const recommendations = recRes.data?.recommendations || []
      if (recommendations.length === 0) {
        addLog('No recommended symbols found, skipping automation', 'warning')
        isCurrentlyRunning.current = false
        return
      }
      addLog(`Received ${recommendations.length} recommendations from Gemini.`, 'info');

      // Step 2: Update stock data for recommended symbols
      const symbolsToFetchData = recommendations.map(r => r.Symbol).filter(Boolean);
      const stockDataResults = await updateStockData(symbolsToFetchData);
      addLog(`Fetched live stock data for ${Object.keys(stockDataResults).length} symbols.`, 'info');

      // Step 3: Build recommendationStocks with fresh OHLC data
      const recommendationStocks = recommendations.map(rec => {
        const symbol = rec.Symbol;
        const stock = stockDataResults[symbol] || {};
        return {
          symbol,
          ltp: stock.ltp ?? rec.EntryPrice, // Use live LTP if available, else recommendation's EntryPrice
          open: stock.open ?? null,
          high: stock.high ?? null,
          low: stock.low ?? null,
          close: stock.close ?? null,
          netChange: stock.netChange ?? null,
          percentChange: stock.percentChange ?? null,
          volume: stock.tradeVolume ?? null,
          signal: rec.Signal,
          confidence: rec.Confidence,
          entryPrice: rec.EntryPrice,
          targetPrice: rec.TargetPrice,
          stopLoss: rec.StopLoss,
          strategy: rec.Strategy,
          reasoning: rec.Reasoning,
        };
      });
      addLog(`Prepared ${recommendationStocks.length} stocks for signal generation.`, 'info');

      // Step 4: Generate signals and execute orders, then place SL/Target orders
      await generateSignalsAndExecuteOrders(recommendationStocks);
      addLog('✅ Automation cycle completed', 'success')
    } catch (error) {
      addLog(`❌ Automation cycle failed: ${error.message}`, 'error')
    } finally {
      isCurrentlyRunning.current = false
    }
  }, [user?.kiteConnected, updateStockData, generateSignalsAndExecuteOrders, addLog])

  // Start automation
  const startAutomation = useCallback(() => {
    if (!user?.kiteConnected) {
      toast.error('Please connect Kite account first')
      return
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    setIsAutomationActive(true)
    addLog('🟢 Market automation started (3-minute intervals)', 'success')
    
    // Run immediately
    runAutomationCycle()
    
    // Set up 3-minute interval — skip if API is paused due to rate limiting
    intervalRef.current = setInterval(() => {
      if (!isApiPaused()) runAutomationCycle()
    }, 3 * 60 * 1000) // 3 minutes

    toast.success('Market automation started')
  }, [user?.kiteConnected, runAutomationCycle, addLog])

  // Stop automation
  const stopAutomation = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    setIsAutomationActive(false)
    addLog('🔴 Market automation stopped', 'warning')
    toast.info('Market automation stopped')
  }, [addLog])

  // Manual trigger
  const triggerManualUpdate = useCallback(async () => {
    if (isCurrentlyRunning.current) {
      toast.info('Automation cycle already running')
      return
    }

    await runAutomationCycle()
  }, [runAutomationCycle])

  // Initialize on mount
  useEffect(() => {
    if (user?.kiteConnected) {
      // Initial fetch of watchlist symbols if needed, though runAutomationCycle will do it
      // fetchWatchlistSymbols()
    }
  }, [user?.kiteConnected])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  // Auto-stop when user disconnects
  useEffect(() => {
    if (!user?.kiteConnected && isAutomationActive) {
      stopAutomation()
    }
  }, [user?.kiteConnected, isAutomationActive, stopAutomation])

  const value = {
    // State
    isAutomationActive,
    stockData,
    watchlistSymbols,
    lastWatchlistUpdate,
    lastSignalGeneration,
    isUpdatingWatchlist,
    isGeneratingSignals,
    automationLogs,
    
    // Actions
    startAutomation,
    stopAutomation,
    triggerManualUpdate,
    updateStockData,
    
    // Utilities
    isRunning: isCurrentlyRunning.current
  }

  return (
    <MarketAutomationContext.Provider value={value}>
      {children}
    </MarketAutomationContext.Provider>
  )
}
